// src/components/MyTickets.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MyTickets.css';

const MyTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replying, setReplying] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // ✅ योग्य API URL - /api prefix सह
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: page,
        limit: pagination.limit
      });

      // ✅ /api prefix सह योग्य URL
      const response = await axios.get(
        `${API_URL}/api/tickets?${params.toString()}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setTickets(response.data.data.items || []);
        setPagination({
          ...pagination,
          page: response.data.data.pagination.page,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages
        });
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      // ✅ /api prefix सह योग्य URL
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticketId}`,
        getAuthHeaders()
      );
      if (response.data.success) {
        setSelectedTicket(response.data.data);
      }
    } catch (err) {
      console.error('Error loading ticket details:', err);
    }
  };

  const handleTicketClick = (ticket) => {
    loadTicketDetails(ticket.id);
  };

  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;
    
    setReplying(true);
    try {
      // ✅ /api prefix सह योग्य URL
      const response = await axios.post(
        `${API_URL}/api/tickets/${ticketId}/replies`,
        { message: replyText },
        getAuthHeaders()
      );
      if (response.data.success) {
        setReplyText('');
        await loadTicketDetails(ticketId);
        await loadTickets(pagination.page);
      }
    } catch (err) {
      console.error('Error adding reply:', err);
      alert(err.response?.data?.message || 'Failed to add reply');
    } finally {
      setReplying(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#3b82f6',
      in_progress: '#f59e0b',
      resolved: '#10b981',
      closed: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBadge = (status) => {
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="my-tickets-container">
      <div className="tickets-header">
        <div>
          <h2>
            <i className="fas fa-ticket-alt"></i>
            My Tickets
          </h2>
          <p className="ticket-count">{pagination.total} tickets</p>
        </div>
        <button className="new-ticket-btn" onClick={() => navigate('/user/raise-ticket')}>
          <i className="fas fa-plus"></i>
          New Ticket
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => loadTickets(pagination.page)}>Retry</button>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>No tickets yet</h3>
          <p>You haven't raised any tickets. Click "New Ticket" to get started.</p>
          <button className="empty-cta" onClick={() => navigate('/user/raise-ticket')}>
            Raise a Ticket
          </button>
        </div>
      ) : (
        <>
          <div className="tickets-grid-3col">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`ticket-card ${selectedTicket?.id === ticket.id ? 'active' : ''}`}
                onClick={() => handleTicketClick(ticket)}
              >
                <div className="ticket-card-header">
                  <span className="ticket-id">{ticket.ticket_no}</span>
                  <span
                    className="status-badge"
                    style={{ background: getStatusColor(ticket.status) }}
                  >
                    {getStatusBadge(ticket.status)}
                  </span>
                </div>
                
                <h4 className="ticket-subject">{ticket.subject}</h4>
                
                <p className="ticket-description-preview">
                  {ticket.description?.substring(0, 60)}...
                </p>
                
                <div className="ticket-meta">
                  <span className="category-tag">
                    <i className="fas fa-tag"></i>
                    {ticket.category || 'general'}
                  </span>
                  <span className="ticket-date">
                    <i className="fas fa-clock"></i>
                    {formatDate(ticket.created_at)}
                  </span>
                </div>
                
                <div className="ticket-footer">
                  <span className="reply-count">
                    <i className="fas fa-comment"></i>
                    {ticket.replies?.length || 0} replies
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => loadTickets(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => loadTickets(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}

          {selectedTicket && (
            <div className="ticket-details-full">
              <div className="ticket-details-header">
                <button 
                  className="close-details-btn"
                  onClick={() => setSelectedTicket(null)}
                >
                  <i className="fas fa-times"></i>
                </button>
                <div>
                  <span className="ticket-id-label">{selectedTicket.ticket_no}</span>
                  <h3>{selectedTicket.subject}</h3>
                </div>
                <div className="ticket-status-actions">
                  <span
                    className="status-badge large"
                    style={{ background: getStatusColor(selectedTicket.status) }}
                  >
                    {getStatusBadge(selectedTicket.status)}
                  </span>
                </div>
              </div>

              <div className="ticket-description">
                <p>{selectedTicket.description}</p>
                <div className="ticket-meta-info">
                  <span>Category: {selectedTicket.category || 'general'}</span>
                  <span>Created: {formatDate(selectedTicket.created_at)}</span>
                  <span>Updated: {formatDate(selectedTicket.updated_at)}</span>
                </div>
              </div>

              <div className="ticket-replies">
                <h4>
                  <i className="fas fa-comments"></i>
                  Conversations ({selectedTicket.replies?.length || 0})
                </h4>
                
                <div className="replies-list">
                  {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                    selectedTicket.replies.map((reply, index) => (
                      <div key={reply.id || index} className="reply-item">
                        <div className="reply-avatar">
                          {(reply.author_name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="reply-content">
                          <div className="reply-header">
                            <strong>{reply.author_name || 'User'}</strong>
                            <span className="reply-role">{reply.author_role || 'user'}</span>
                            <span className="reply-time">{formatDate(reply.created_at)}</span>
                          </div>
                          <p>{reply.message}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-replies">
                      <i className="fas fa-comment-slash"></i>
                      <span>No replies yet</span>
                    </div>
                  )}
                </div>

                {selectedTicket.status !== 'closed' && (
                  <div className="reply-input">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows="3"
                      disabled={replying}
                    />
                    <button
                      onClick={() => handleReply(selectedTicket.id)}
                      disabled={!replyText.trim() || replying}
                    >
                      {replying ? (
                        <><i className="fas fa-spinner fa-spin"></i> Sending...</>
                      ) : (
                        <><i className="fas fa-paper-plane"></i> Send Reply</>
                      )}
                    </button>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="closed-message">
                    <i className="fas fa-lock"></i>
                    This ticket is closed.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyTickets;